<template>
  <div class="container mt-4">
    <div class="row">
      <div class="col-12">
        <div
          v-if="error"
          class="alert alert-danger mt-2"
        >
          {{ error }}
        </div>
        <div
          v-if="loading"
          class="my-2"
        >
          <small>{{ $t('tokenManagement.loading') }}</small>
        </div>
      </div>
    </div>

    <DxDataGrid
      :data-source="tokenDataSource"
      :show-borders="true"
      :row-alternation-enabled="true"
      class="mb-3"
      @rowPrepared="onRowPrepared"
      no-data-text=""
    >
      <DxColumnFixing :enabled="true" />
      <DxFilterRow :visible="false" />
      <DxHeaderFilter :visible="false" />
      <DxSearchPanel
        :visible="false"
        :highlight-search-text="true"
      />
      <DxSelection mode="single" />
      <DxPaging :page-size="1" />
      <DxPager
        :show-info="true"
        :show-navigation-buttons="true"
        :show-page-size-selector="true"
        :allowed-page-sizes="[1, 2, 5]"
      />

      <DxColumn
        data-field="refreshTokenHash"
        :caption="$t('tokenManagement.currentToken')"
        cell-template="tokenCellTemplate"
        :allow-resizing="true"
        :width="columnWidths.token"
        :min-width="isMobile ? 150 : 400"
      />

      <DxColumn
        data-field="expiresAt"
        :caption="$t('tokenManagement.expiresAt')"
        :alignment="'center'"
        :width="columnWidths.expiresAt"
      />

      <DxColumn
        data-field="status"
        :caption="$t('tokenManagement.status')"
        :alignment="'center'"
        :width="columnWidths.status"
      />

      <DxColumn
        v-if="showActionsColumn"
        :caption="''"
        :alignment="'center'"
        :width="columnWidths.actions"
        cell-template="refreshActionCell"
      />

      <template #tokenCellTemplate="{ data }">
        <span class="text-break">{{ data.data.refreshTokenHash }}</span>
      </template>

      <template #refreshActionCell>
        <button
          class="btn btn-primary btn-sm"
          @click="generateNewToken"
        >
          {{ $t('tokenManagement.generateNewToken') }}
        </button>
      </template>
    </DxDataGrid>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  DxDataGrid,
  DxColumn,
  DxColumnFixing,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSearchPanel,
  DxSelection
} from 'devextreme-vue/data-grid'
import { getClientRefreshApiToken } from '@/crud/getters'
import { postCreateApiToken } from '@/crud/senders'
import { getHumanDateTimeFromDateTime } from '@/utils/tools'

export default defineComponent({
  name: 'TokenManagement',
  components: {
    DxDataGrid,
    DxColumn,
    DxColumnFixing,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxSearchPanel,
    DxSelection
  },
  setup() {
    const i18n = useI18n()
    const refreshTokenHash = ref<string | null>(null)
    const expiresAt = ref<Date | null>(null)
    const error = ref<string | null>(null)
    const loading = ref<boolean>(false)
    const windowWidth = ref<number>(window.innerWidth)

    const handleResize = () => {
      windowWidth.value = window.innerWidth
    }

    const isMobile = computed(() => {
      return windowWidth.value <= 768
    })

    const columnWidths = computed(() => {
      if (isMobile.value) {
        return {
          token: 150,
          expiresAt: 150,
          status: 200,
          actions: 120
        }
      } else {
        return {
          token: 600,
          expiresAt: 200,
          status: 200,
          actions: 230
        }
      }
    })

    const fetchToken = async () => {
      loading.value = true
      error.value = null
      refreshTokenHash.value = null
      expiresAt.value = null
      try {
        const response = await getClientRefreshApiToken()
        refreshTokenHash.value = response.data.refresh_token_hash
        if (response.data.expires_at) {
          expiresAt.value = new Date(response.data.expires_at)
        }
      } catch (err: any) {
        error.value = i18n.t('tokenManagement.errorFetch') as string
      } finally {
        loading.value = false
      }
    }

    const generateNewToken = async () => {
      loading.value = true
      error.value = null
      try {
        await postCreateApiToken()
        await fetchToken()
      } catch (err: any) {
        error.value = i18n.t('tokenManagement.errorCreate') as string
      } finally {
        loading.value = false
      }
    }

    const isTokenExpired = computed(() => {
      if (!expiresAt.value) return true
      return new Date() > expiresAt.value
    })

    const isNoToken = computed(() => {
      return !refreshTokenHash.value && !expiresAt.value
    })

    const tokenStatus = computed(() => {
      if (isNoToken.value) return '---'
      return isTokenExpired.value
          ? i18n.t('tokenManagement.expired')
          : i18n.t('tokenManagement.active')
    })

    const tokenColor = computed(() => {
      if (isNoToken.value) return 'red'
      if (tokenStatus.value === i18n.t('tokenManagement.expired')) return 'red'
      if (!expiresAt.value) return 'red'
      const now = new Date()
      const diff = expiresAt.value.getTime() - now.getTime()
      if (diff <= 0) return 'red'
      const daysLeft = diff / (1000 * 60 * 60 * 24)
      return daysLeft <= 30 ? '#252422' : 'green'
    })

    const formattedExpiresAt = computed(() => {
      if (isNoToken.value) return '---'
      if (!expiresAt.value) return '-'
      return getHumanDateTimeFromDateTime(expiresAt.value.toISOString())
    })

    const tokenDataSource = computed(() => {
      if (isNoToken.value) {
        return [
          {
            refreshTokenHash: i18n.t('tokenManagement.noToken'),
            expiresAt: '---',
            status: '---'
          }
        ]
      }
      const status = tokenStatus.value
      return [
        {
          refreshTokenHash: refreshTokenHash.value,
          expiresAt: formattedExpiresAt.value,
          status
        }
      ]
    })

    const showActionsColumn = computed(() => {
      if (isNoToken.value) return true
      const [record] = tokenDataSource.value
      return record.status === i18n.t('tokenManagement.expired')
    })

    const onRowPrepared = (e: any) => {
      if (e.rowType === 'data') {
        e.rowElement.style.color = tokenColor.value
      }
    }

    onMounted(() => {
      fetchToken()
      window.addEventListener('resize', handleResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

    return {
      refreshTokenHash,
      expiresAt,
      error,
      loading,
      tokenDataSource,
      isTokenExpired,
      isNoToken,
      tokenColor,
      tokenStatus,
      formattedExpiresAt,
      fetchToken,
      generateNewToken,
      showActionsColumn,
      onRowPrepared,
      columnWidths,
      isMobile,
      i18n
    }
  }
})
</script>

<style scoped>
.dx-datagrid {
  font-size: 0.9rem;
}
.text-break {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
</style>
